import React, { useState, useEffect, useCallback } from 'react';
import nbrApi from '../../../services/nbrApi';
import moment from 'moment';
import { Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../../css/multi-property-monitor.css';

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' }
];

function ReservationReport() {
    const selectedLoginData = JSON.parse(localStorage.getItem('selectedLoginData')) || {};
    const propertiesId = selectedLoginData?.properties_id;

    // State management
    const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    const [activeRange, setActiveRange] = useState('today');
    const [reservationData, setReservationData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Apply date range presets
    const applyDateRange = (rangeKey) => {
        switch (rangeKey) {
            case 'today': {
                const todayStart = moment().startOf('day');
                setFromDate(todayStart.format('YYYY-MM-DD'));
                setToDate(todayStart.format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_week': {
                const startOfWeek = moment().startOf('isoWeek');
                setFromDate(startOfWeek.format('YYYY-MM-DD'));
                setToDate(startOfWeek.clone().endOf('isoWeek').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_month': {
                const startOfMonth = moment().startOf('month');
                setFromDate(startOfMonth.format('YYYY-MM-DD'));
                setToDate(startOfMonth.clone().endOf('month').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            default:
                break;
        }
    };

    // Fetch reservation counts data
    const fetchReportData = useCallback(async () => {
        if (!propertiesId) {
            toast.error('Property ID not found. Please log in again.');
            return;
        }

        setLoading(true);
        try {
            const response = await nbrApi.post('/getreservationcounts', {
                properties_id: propertiesId,
                date_from: fromDate,
                date_to: toDate
            });

            if (response?.data && Array.isArray(response.data)) {
                setReservationData(response.data);
                if (response.data.length === 0) {
                    toast.info('No reservation data found for the selected date range');
                } else {
                    toast.success(`Loaded ${response.data.length} reservation record(s)`);
                }
            } else {
                setReservationData([]);
                toast.info('No reservation data found for the selected date range');
            }
        } catch (error) {
            console.error('Error fetching reservation data:', error);
            toast.error('Failed to fetch reservation data. Please try again.');
            setReservationData([]);
        } finally {
            setLoading(false);
        }
    }, [propertiesId, fromDate, toDate]);

    // Initialize with today's range
    useEffect(() => {
        applyDateRange('today');
    }, [propertiesId]);

    // Auto-fetch data when dates change
    useEffect(() => {
        if (propertiesId) {
            fetchReportData();
        }
    }, [propertiesId, fromDate, toDate, fetchReportData]);

    // Get table columns dynamically from first record if available
    const getTableColumns = () => {
        if (reservationData.length === 0) return [];
        
        const firstRecord = reservationData[0];
        return Object.keys(firstRecord).filter(key => key !== 'id'); // Exclude id if present
    };

    const formatCellValue = (value, columnKey) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') return JSON.stringify(value);
        
        // Format date fields
        if (columnKey && (columnKey.toLowerCase().includes('date') || columnKey.toLowerCase().includes('time'))) {
            const date = moment(value);
            if (date.isValid()) {
                return date.format('YYYY-MM-DD');
            }
        }
        
        return value;
    };

    const formatColumnHeader = (key) => {
        // Convert snake_case or camelCase to Title Case
        return key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <h3 className="mb-0 text-primary fw-bold">
                                <i className="bi bi-calendar-check me-2"></i>
                                Reservation Report
                            </h3>
                            <p className="text-muted mb-0 mt-2">
                                View reservation counts by date for the selected property
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="row mb-2">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-end g-3">
                                {/* From Date */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label htmlFor="fromDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-event me-2"></i>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        id="fromDate"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* To Date */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label htmlFor="toDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        id="toDate"
                                        className="form-control"
                                        value={toDate}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>

                                {/* Quick Ranges */}
                                <div className="col-12 col-md-6 col-lg-4">
                                    <label className="form-label fw-bold text-primary d-block">
                                        <i className="bi bi-lightning-charge me-2"></i>
                                        Quick Ranges
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {RANGE_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`btn btn-sm ${activeRange === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => applyDateRange(option.value)}
                                                style={{ flex: '1 1 auto', minWidth: '100px' }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Generate Button */}
                                <div className="col-12 col-md-6 col-lg-2">
                                    <label className="form-label fw-bold text-primary d-block d-lg-none">
                                        &nbsp;
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary w-100"
                                        onClick={fetchReportData}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-search me-2"></i>
                                        {loading ? 'Loading...' : 'Generate'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0" style={{ position: 'relative' }}>
                        <div className="card-body" style={{ position: 'relative', minHeight: '300px' }}>
                            {loading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 10,
                                    borderRadius: '0.375rem'
                                }}>
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading, please wait...</p>
                                </div>
                            )}
                            {reservationData.length > 0 ? (
                                <div className="table-responsive">
                                    <Table responsive bordered hover className="table shadow-hover mb-4 dataTable no-footer" style={{ backgroundColor: '#fff' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                {getTableColumns().map((column, index) => (
                                                    <th key={index} className="fs-14 font-w600 border-0 text-primary" style={{ backgroundColor: '#f8f9fa' }}>
                                                        {formatColumnHeader(column)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reservationData.map((record, rowIndex) => (
                                                <tr key={rowIndex} style={{ backgroundColor: '#fff' }}>
                                                    {getTableColumns().map((column, colIndex) => (
                                                        <td key={colIndex} className="fs-14" style={{ color: '#000', backgroundColor: '#fff' }}>
                                                            {formatCellValue(record[column], column)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                !loading && (
                                    <div className="text-center py-5">
                                        <i className="bi bi-calendar-x" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                        <p className="text-muted mt-3">
                                            No reservation data available for the selected date range
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReservationReport;

